import React, { useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ArrowUpRight } from 'lucide-react';

interface FollowUp {
  label: string;
  prompt: string;
}

function deriveFollowUps(text: string): FollowUp[] {
  const t = text.toLowerCase();

  const sets: Array<{ keywords: string[]; chips: FollowUp[] }> = [
    {
      keywords: ['pr', 'pull request', 'merge', 'merged', 'cycle time', 'sprint', 'velocity'],
      chips: [
        { label: 'Who opened the most PRs?', prompt: 'Who opened the most pull requests this sprint?' },
        { label: 'Any PRs stalled?', prompt: 'Which pull requests are stalled or have had no activity in the past 3 days?' },
        { label: 'Compare to last sprint', prompt: 'Compare this sprint\'s metrics to last sprint.' },
      ],
    },
    {
      keywords: ['contributor', 'top contributor', 'commit', 'author', 'member', 'team'],
      chips: [
        { label: 'Show their recent activity', prompt: 'Show me the recent activity for the top contributor.' },
        { label: 'Any members idle?', prompt: 'Which team members have had no activity in the past week?' },
        { label: 'Workload distribution', prompt: 'How is work distributed across the team this sprint?' },
      ],
    },
    {
      keywords: ['standup', 'morning', 'brief', 'summary', 'yesterday', 'today', 'blocker'],
      chips: [
        { label: 'Export as Slack post', prompt: 'Format this standup as a Slack message I can copy and paste.' },
        { label: 'Expand blockers', prompt: 'Tell me more about the blockers mentioned.' },
        { label: 'Who can unblock this?', prompt: 'Who is best placed to resolve the blockers mentioned?' },
      ],
    },
    {
      keywords: ['alert', 'failing', 'error', 'issue', 'open', 'bug', 'incident', 'health'],
      chips: [
        { label: 'Which is highest priority?', prompt: 'Which of these open issues is the highest priority?' },
        { label: 'Who owns the alerts?', prompt: 'Who is responsible for the open alerts or failing checks?' },
        { label: 'How long have they been open?', prompt: 'How long have these issues been open?' },
      ],
    },
    {
      keywords: ['stream', 'project', 'repository', 'repo', 'service'],
      chips: [
        { label: 'Health overview', prompt: 'Give me a health overview of this stream or project.' },
        { label: 'Recent changes', prompt: 'What changed in this stream or project in the last 7 days?' },
        { label: 'Open work items', prompt: 'What open work items are in this stream or project?' },
      ],
    },
  ];

  for (const set of sets) {
    if (set.keywords.some((kw) => t.includes(kw))) {
      return set.chips.slice(0, 3);
    }
  }

  return [
    { label: 'Drill deeper', prompt: 'Can you go into more detail on this?' },
    { label: 'Summarise as bullet points', prompt: 'Summarise that as a concise bullet list.' },
    { label: 'What should I do next?', prompt: 'Based on that, what should I focus on next?' },
  ];
}

interface VantiFollowUpChipsProps {
  messages: any[];
  loading: boolean;
  onSend: (text: string) => void;
}

export default function VantiFollowUpChips({ messages, loading, onSend }: VantiFollowUpChipsProps) {
  const lastAssistantMsg = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.role === 'assistant' || m.sender === 'assistant') return m;
    }
    return null;
  }, [messages]);

  const chips = useMemo(() => {
    if (!lastAssistantMsg) return [];
    const content = lastAssistantMsg.content ?? lastAssistantMsg.text ?? '';
    if (typeof content !== 'string' || content.length < 20) return [];
    return deriveFollowUps(content);
  }, [lastAssistantMsg]);

  const visible = !loading && chips.length > 0;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 4 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className="shrink-0 border-t border-dark-border/50 bg-dark-base/60 px-5 py-2.5"
        >
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] font-medium uppercase tracking-wider text-slate-700">
              Follow up
            </span>
            {chips.map((chip) => (
              <button
                key={chip.label}
                type="button"
                onClick={() => onSend(chip.prompt)}
                className="flex items-center gap-1 rounded-full border border-dark-border bg-dark-elevated px-3 py-1 text-[11px] text-slate-400 transition-all hover:border-brand-purple/40 hover:bg-brand-purple/10 hover:text-brand-purple-light"
              >
                {chip.label}
                <ArrowUpRight className="h-2.5 w-2.5 shrink-0" aria-hidden />
              </button>
            ))}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
